import {
  AdminHeader,
  AdminSingleBrandPicker,
  AdminSingleLocationPicker,
  AdminSpinner,
  Box,
  BrandType,
  Div,
  H3,
  H5,
  Icon,
  Img,
  P,
  SimpleBrandEntity,
  TrashIconRed,
  calendarImage,
  getAllClosingDays,
  getBrandById,
  isAdmin,
  isPartner,
  lightTheme,
  read,
  store,
  reportError,
} from '@vgrubs/components';
import {
  SimpleLocationEntity,
  BrandClosingDayEntity,
} from 'components/src/types/location.types';
import { getLocationsByIds } from 'components/src/api/locations.api';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { useSearchParams } from 'react-router-dom';
import { AddClosingDays } from './AddClosingDays';
import { ClosingDaysDelete } from './ClosingDaysDelete';

export const ClosingDays = () => {
  const [loading, setLoading] = useState(false);
  const [addModal, setAddModal] = useState<boolean>(false);
  const [closingDays, setClosingDays] = useState<BrandClosingDayEntity[]>([]);
  const [timezone, setTimezone] = useState('');
  const locationLSNode = 'vgrubs-admin-closing-days-location';
  const locationFromLS = JSON.parse(read(locationLSNode) as string);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [ref, inView] = useInView();
  const [deleteModal, setDeleteModal] = useState<BrandClosingDayEntity | null>(
    null
  );

  const [selectedLocation, setSelectedLocation] = useState(
    locationFromLS || null
  );
  const [selectedBrand, setSelectedBrand] = useState<SimpleBrandEntity | null>(
    null
  );

  const [searchParams, setSearchParams] = useSearchParams();
  const [loadingInitialUrlData, setLoadingInitialUrlData] = useState(() =>
    Boolean(searchParams.get('locationIds') || searchParams.get('brandIds'))
  );

  useEffect(() => {
    const locationIdsParam = searchParams.get('locationIds');
    const brandIdsParam = searchParams.get('brandIds');

    if (!locationIdsParam && !brandIdsParam) return;

    const parseFirstId = (value: string | null) => {
      if (!value) return null;
      const id = parseInt(value.split(',')[0]?.trim() ?? '', 10);
      return Number.isNaN(id) ? null : id;
    };

    const locationId = parseFirstId(locationIdsParam);
    const brandId = parseFirstId(brandIdsParam);

    async function loadFromUrl() {
      try {
        const [locationRes, brandRes] = await Promise.all([
          locationId ? getLocationsByIds([locationId]) : Promise.resolve(null),
          brandId ? getBrandById(brandId) : Promise.resolve(null),
        ]);

        const location = locationRes?.data.data[0] ?? null;
        const brand = brandRes?.data.data ?? null;

        if (location) {
          store(locationLSNode, JSON.stringify(location));
          setSelectedLocation(location);
        }

        if (brand) {
          const simpleBrand: SimpleBrandEntity = {
            id: brand.id,
            name: brand.name,
            isLocation: brand.isLocation,
            isVGrubsBrand: brand.type === BrandType.vGrubs,
          };
          setSelectedBrand(simpleBrand);
        } else {
          setSelectedBrand(null);
        }
      } catch (error) {
        reportError(error);
      } finally {
        setLoadingInitialUrlData(false);
        setSearchParams(
          (prev) => {
            prev.delete('locationIds');
            prev.delete('brandIds');
            return prev;
          },
          { replace: true }
        );
      }
    }

    loadFromUrl();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function getClosingDays(
    page: number,
    hasMore: boolean,
    reset?: boolean
  ) {
    if (!hasMore) return;

    try {
      setLoading(true);
      const { data } = await getAllClosingDays({
        page: page,
        limit: 20,
        locationId: selectedLocation?.id,
        brandId: selectedBrand?.id ?? null,
      });

      if (data.data.items[0]) {
        setPage((prevPage) => prevPage + 1);
        setHasMore(true);
      } else {
        setHasMore(false);
      }

      if (!reset) {
        setClosingDays((prevItems) => [...prevItems, ...data.data.items]);
      } else {
        setClosingDays(data.data.items);
      }

      setTimezone(data.data.timeZone);
      setLoading(false);
    } catch (error) {
      reportError(error);
      setLoading(false);
    }
  }

  useEffect(() => {
    if (selectedLocation || selectedBrand) {
      getClosingDays(1, true);
      setPage(1);
      setClosingDays([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocation, selectedBrand]);

  useEffect(() => {
    if (inView && closingDays.length > 0) {
      getClosingDays(page, hasMore);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView]);

  const handleAddSubmit = () => {
    getClosingDays(1, true, true);
    setPage(1);
  };

  const handleLocationsSubmit = (l: SimpleLocationEntity) => {
    store(locationLSNode, JSON.stringify(l));
    setSelectedBrand(null);
  };

  const formatDateToDayOfWeek = (
    dateString: string,
    targetTimezone?: string
  ) => {
    // Create a Date object from the input string
    const inputDate = new Date(dateString);

    // Convert the date to the target timezone
    const options = { timeZone: targetTimezone };
    inputDate.toLocaleString('en-US', options);

    // Get the day of the week
    const dayOfWeekNumber = inputDate.getDay();
    const daysOfWeek = [
      'Sunday',
      'Monday',
      'Tuesday',
      'Wednesday',
      'Thursday',
      'Friday',
      'Saturday',
    ];
    const dayOfWeekName = daysOfWeek[dayOfWeekNumber];

    return dayOfWeekName;
  };

  return (
    <>
      {loading && <AdminSpinner />}
      <AdminHeader title={'Closing Days'}>
        <AddClosingDays
          closeModal={() => {
            setAddModal(false);
          }}
          setLoading={setLoading}
          locationId={selectedLocation?.id}
          brandId={selectedBrand?.id ?? null}
          handleAddSubmit={handleAddSubmit}
        />
      </AdminHeader>
      <Div>
        <Div $m="32px 0" $mSM="20px 0" $flex $gap="24px" $gapSM="20px" $column>
          {!loadingInitialUrlData && (
            <Div $flex $gap="24px" $gapSM="8px" $columnSM>
              <Div $widthSM="100%" $width={isAdmin ? '50%' : '100%'}>
                <AdminSingleLocationPicker
                  selectedLocation={selectedLocation}
                  setSelectedLocation={setSelectedLocation}
                  handleSubmit={(l: SimpleLocationEntity) =>
                    handleLocationsSubmit(l)
                  }
                />
              </Div>
              {isAdmin && (
                <Div $widthSM="100%" $width="50%">
                  <AdminSingleBrandPicker
                    selectedLocation={selectedLocation}
                    selectedBrand={selectedBrand}
                    setSelectedBrand={setSelectedBrand}
                  />
                </Div>
              )}
            </Div>
          )}

          {closingDays && (
            <div>
              {closingDays.map((day, i) => {
                return (
                  <Box key={i} $flex $alignCenter $between $mb="8">
                    <Div>
                      <P $mb="0">{formatDateToDayOfWeek(day.date)}</P>
                      <H5 $mb="0">
                        {DateTime.fromISO(day.date)
                          .setZone(timezone)
                          .toFormat('MMM dd, yyyy')}
                      </H5>
                    </Div>
                    <Icon
                      $pointer
                      $background={lightTheme.white}
                      onClick={() => setDeleteModal(day)}
                    >
                      <TrashIconRed />
                    </Icon>
                  </Box>
                );
              })}
            </div>
          )}

          {closingDays.length < 1 && !loading && (
            <Div $flex $center $column $mt="100">
              <Img src={calendarImage} alt=" No closing days!" $width="200px" />
              <H3 $color={lightTheme.primary} $mb="20">
                No closing days!
              </H3>
              <P $bold $center>
                This location currently doesn't <br /> have closed days.
              </P>
            </Div>
          )}
        </Div>
        <div ref={ref}></div>

        <ClosingDaysDelete
          setLoading={setLoading}
          closeModal={() => {
            setDeleteModal(null);
          }}
          modalIsOpen={deleteModal}
          setClosingDays={setClosingDays}
        />
      </Div>
    </>
  );
};
